import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import { alertMessage } from "../actions/common";
import { errorHandler } from "app/shared/Service/errorHandler";
import { createQuery, getActiveConfig } from "app/utils/constants/common";
import { RootState } from "../reducers/hooks";
import { PaginationInterfaceLF } from "app/types";
import {
  getBundleByStoreAndPackageId,
  getBundleUnitsByStoreAndPackageId,
  getCoreMacrosOptionsAPI,
  getCreditPackagesApi,
  getPackagesByIdApi,
  getPackagesListApi,
  getPortionsApi,
  postBundlesApi,
  postBundleUnitsApi,
  postCoreMacrosPricingAPI,
  postCreditPackagesApi,
  postPackagesApi,
  putBundleApi,
  putBundleUnitApi,
  putCreditPackagesApi,
  putPackagesApi,
  updateMacroMealsApi,
} from "app/services/packages";
import {
  CoreMacrosOptionsData,
  CorePackagePrice,
  CreatePackagePayload,
  CreditPackageInterface,
  CreditPackagePayload,
  PackagesInterface,
  PackageType,
  PortionData,
  PortionFilter,
  PostBundlePayload,
  PostCorePricingApiResponse,
  UpdateMacroMealsPayload,
} from "app/types/package";
import {
  Bundle,
  BundleUnitByIdInterface,
  BundleUnitPayload,
  Currency,
  Portion,
} from "app/types/common";
import { UPDATE_VIEW_SUBSCRIPTION_MEAL } from "app/utils/constants/subscriptions";

interface PackagesFilters {
  type?: string;
  search?: string;
}

interface InitialState {
  packagesList: PackagesInterface[];
  packagesListPagination: PaginationInterfaceLF;
  packagesListRowsPerPAge: number;
  isPackagesListLoading: boolean;
  packagesDetailsLoading: boolean;
  filters: PackagesFilters | null;
  portionList: Array<{
    brandId: number;
    list: PortionData[];
  }>;

  coreMacrosOptionsData: CoreMacrosOptionsData | null;
  corePackagePriceData: CorePackagePrice | null;
  bundleList: Bundle[];
  bundleListPagination: PaginationInterfaceLF | null;
  bundleListRowsPerPage: number;
  isBundleListLoading: boolean;
  isSubmittingBundle: boolean;

  bundleUnits: BundleUnitByIdInterface[];
  bundleUnitsPagination: PaginationInterfaceLF | null;
  bundleUnitsRowsPerPage: number;
  isBundleUnitsLoading: boolean;
  isSubmittingBundleUnits: boolean;
  bundleUnitsIncluded: {
    currencies: Currency[];
    portions: Portion[];
  };

  creditPackageList: CreditPackageInterface[];
  creditPackageListPagination: PaginationInterfaceLF | null;
  creditPackageListRowsPerPage: number;
  isCreditPackageListLoading: boolean;
  isSubmittingCreditPackage: boolean;
}

const initialState: InitialState = {
  packagesList: [],
  packagesListRowsPerPAge: 20,
  packagesListPagination: null,
  isPackagesListLoading: false,
  packagesDetailsLoading: false,
  filters: null,
  portionList: [],
  bundleList: [],
  bundleListPagination: null,
  bundleListRowsPerPage: 20,
  isBundleListLoading: false,
  isSubmittingBundle: false,

  bundleUnits: [],
  bundleUnitsIncluded: null,
  bundleUnitsPagination: null,
  bundleUnitsRowsPerPage: 20,
  isBundleUnitsLoading: false,
  isSubmittingBundleUnits: false,
  coreMacrosOptionsData: null,
  corePackagePriceData: null,

  creditPackageList: [],
  creditPackageListPagination: null,
  creditPackageListRowsPerPage: 20,
  isCreditPackageListLoading: false,
  isSubmittingCreditPackage: false,
};

export const packages = createSlice({
  name: "packages",
  initialState,
  reducers: {
    setPackagesList: (
      state,
      action: PayloadAction<{
        list: PackagesInterface[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.packagesList = action.payload.list;
      state.packagesListPagination = action.payload.pagination;
    },

    setPackagesListLoading: (state, action: PayloadAction<boolean>) => {
      state.isPackagesListLoading = action.payload;
    },
    setPackagesListRowPerPage: (state, action: PayloadAction<number>) => {
      state.packagesListRowsPerPAge = action.payload;
    },
    setPackagesDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.packagesDetailsLoading = action.payload;
    },
    setPackagesFilters: (
      state,
      action: PayloadAction<PackagesFilters | null>,
    ) => {
      state.filters = action.payload;
    },

    setPortionList: (
      state,
      action: PayloadAction<{
        brandId: number;
        list: PortionData[];
      }>,
    ) => {
      const { brandId, list } = action.payload;
      const index = state.portionList.findIndex(
        (item) => item.brandId === brandId,
      );

      if (index > -1) {
        const existingList = state.portionList[index].list;
        const merged = [...existingList, ...list];

        const uniqueList = Array.from(
          new Map(merged.map((p) => [p.id, p])).values(),
        );
        state.portionList[index].list = uniqueList;
      } else {
        state.portionList.push({
          brandId,
          list,
        });
      }
    },

    setBundleList: (
      state,
      action: PayloadAction<{
        list: Bundle[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.bundleList = action.payload.list;
      state.bundleListPagination = action.payload.pagination;
    },

    setBundleListLoading: (state, action: PayloadAction<boolean>) => {
      state.isBundleListLoading = action.payload;
    },
    setBundleListRowPerPage: (state, action: PayloadAction<number>) => {
      state.bundleListRowsPerPage = action.payload;
    },
    setSubmittingBundle: (state, action: PayloadAction<boolean>) => {
      state.isSubmittingBundle = action.payload;
    },

    setBundleUnits: (
      state,
      action: PayloadAction<{
        list: BundleUnitByIdInterface[];
        pagination: PaginationInterfaceLF;
        included: {
          currencies: Currency[];
          portions: Portion[];
        };
      }>,
    ) => {
      state.bundleUnits = action.payload.list;
      state.bundleUnitsPagination = action.payload.pagination;
      state.bundleUnitsIncluded = action.payload.included;
    },

    setBundleUnitsLoading: (state, action: PayloadAction<boolean>) => {
      state.isBundleUnitsLoading = action.payload;
    },
    setBundleUnitsRowPerPage: (state, action: PayloadAction<number>) => {
      state.bundleUnitsRowsPerPage = action.payload;
    },
    setSubmittingBundleUnits: (state, action: PayloadAction<boolean>) => {
      state.isSubmittingBundleUnits = action.payload;
    },
    setCoreMacrosOptionsData: (
      state,
      action: PayloadAction<CoreMacrosOptionsData>,
    ) => {
      state.coreMacrosOptionsData = action.payload;
    },
    setCorePackagePriceData: (
      state,
      action: PayloadAction<CorePackagePrice>,
    ) => {
      state.corePackagePriceData = action.payload;
    },

    setCreditPackageList: (
      state,
      action: PayloadAction<{
        list: CreditPackageInterface[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.creditPackageList = action.payload.list;
      state.creditPackageListPagination = action.payload.pagination;
    },
    setCreditPackageListLoading: (state, action: PayloadAction<boolean>) => {
      state.isCreditPackageListLoading = action.payload;
    },
    setCreditPackageListRowPerPage: (state, action: PayloadAction<number>) => {
      state.creditPackageListRowsPerPage = action.payload;
    },
    setSubmittingCreditPackage: (state, action: PayloadAction<boolean>) => {
      state.isSubmittingCreditPackage = action.payload;
    },
    clearCorePackagePriceData: (state) => {
      state.corePackagePriceData = null;
    },
  },
});

export const {
  setPackagesList,
  setPackagesListLoading,
  setPackagesListRowPerPage,
  setPackagesDetailsLoading,
  setPackagesFilters,

  setPortionList,
  setBundleList,
  setBundleListLoading,
  setBundleListRowPerPage,
  setSubmittingBundle,

  setBundleUnitsLoading,
  setBundleUnits,
  setBundleUnitsRowPerPage,
  setSubmittingBundleUnits,
  setCoreMacrosOptionsData,
  setCorePackagePriceData,
  clearCorePackagePriceData,

  setCreditPackageList,
  setCreditPackageListLoading,
  setCreditPackageListRowPerPage,
  setSubmittingCreditPackage,
} = packages.actions;

export default packages.reducer;

export const getPackagesListAction = (
  { continutationToken }: { continutationToken?: string | null } = {
    continutationToken: null,
  },
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { storeId } = getActiveConfig();
      const { filters, packagesListRowsPerPAge } = getState().packages;
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            ...(filters || {}),
            limit: packagesListRowsPerPAge,
            continutationToken,
          },
        });
      dispatch(setPackagesListLoading(true));
      const { data } = await getPackagesListApi(storeId, query);
      dispatch(setPackagesListLoading(false));
      const packages = data.data;
      const pagination = data.pagination;
      const groups = data.included?.groups || [];
      // convert groups to Map
      const groupsMap = new Map(groups.map((group) => [group.groupId, group]));
      packages.forEach((pkg) => {
        pkg.group = groupsMap.get(pkg.groupId);
      });
      dispatch(
        setPackagesList({
          list: packages,
          pagination,
        }),
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setPackagesListLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };
};

type CreatePackageRequestParam = {
  storeId: number;
  payload: CreatePackagePayload;
};
export const createPackagesAction = ({
  storeId,
  payload,
}: CreatePackageRequestParam) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await postPackagesApi(storeId, payload);
      if (data.data) {
        dispatch(alertMessage("Created successfully", "success"));
        dispatch(getPackagesListAction({ continutationToken: null }));
      }
      return data;
    } catch (error) {
      dispatch(alertMessage("Something went wrong", "error"));
    }
  };
};

type UpdatePackageRequestParam = {
  storeId: number;
  packageId: number;
  payload: CreatePackagePayload;
};

export const updatePackagesAction =
  ({ storeId, packageId, payload }: UpdatePackageRequestParam) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setPackagesDetailsLoading(true));
      const { data } = await putPackagesApi(storeId, packageId, payload);
      dispatch(setPackagesDetailsLoading(false));
      if (data.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        dispatch(getPackagesListAction({ continutationToken: null }));
        return data;
      }
    } catch (error) {
      dispatch(setPackagesDetailsLoading(false));
      dispatch(alertMessage("Something went wrong", "error"));
    }
  };

type GetBundlesActionParams = {
  packageId: number;
  storeId: number;
  continutationToken?: string | null;
};
export const getBundlesAction =
  ({ packageId, storeId, continutationToken = null }: GetBundlesActionParams) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<Bundle[]> => {
    try {
      const { bundleListRowsPerPage } = getState().packages;
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            limit: bundleListRowsPerPage,
            continutationToken,
          },
        });
      dispatch(setBundleListLoading(true));
      const { data } = await getBundleByStoreAndPackageId({
        storeId,
        packageId,
        query,
      });
      dispatch(setBundleListLoading(false));
      const bundles = data.data;
      const pagination = data.pagination;

      dispatch(
        setBundleList({
          list: bundles,
          pagination,
        }),
      );

      return data.data;
    } catch (error) {
      dispatch(setBundleListLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };

type PostBundleActionParams = {
  packageId: number;
  storeId: number;
  payload: PostBundlePayload;
};
export const postBundleAction =
  ({ packageId, storeId, payload }: PostBundleActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingBundle(true));
      const { data } = await postBundlesApi({ packageId, storeId, payload });
      dispatch(setSubmittingBundle(false));
      if (data?.data) {
        dispatch(alertMessage("Created successfully", "success"));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
      dispatch(setSubmittingBundle(false));
    }
  };

export const putBundleAction =
  ({
    packageId,
    bundleId,
    storeId,
    payload,
  }: PostBundleActionParams & { bundleId: number }) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingBundle(true));
      const { data } = await putBundleApi({
        packageId,
        bundleId,
        storeId,
        payload,
      });
      dispatch(setSubmittingBundle(false));
      if (data?.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
      dispatch(setSubmittingBundle(false));
    }
  };

export const getBundleUnitsAction = ({
  storeId,
  packageId,
  bundleId,
  continutationToken = null,
}: {
  storeId: number;
  packageId: number;
  bundleId: number;
  continutationToken?: string | null;
}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { bundleUnitsRowsPerPage } = getState().packages;
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            limit: bundleUnitsRowsPerPage,
            continutationToken,
          },
        });
      dispatch(setBundleUnitsLoading(true));
      const { data } = await getBundleUnitsByStoreAndPackageId({
        storeId,
        packageId,
        bundleId,
        query: query,
      });
      dispatch(setBundleUnitsLoading(false));
      const pagination = data.pagination;
      const bundleUnits = data.data;
      const included = data.included;

      dispatch(
        setBundleUnits({
          list: bundleUnits,
          pagination,
          included,
        }),
      );
      return data.data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
      dispatch(setBundleUnitsLoading(false));
    }
  };
};

export const postBundleUnitAction = ({
  storeId,
  packageId,
  bundleId,
  payload,
}: {
  storeId: number;
  packageId: number;
  bundleId: number;
  payload: BundleUnitPayload;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingBundleUnits(true));
      const { data } = await postBundleUnitsApi({
        storeId,
        packageId,
        bundleId,
        payload,
      });
      dispatch(setSubmittingBundleUnits(false));
      if (data?.data) {
        dispatch(alertMessage("Created successfully", "success"));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
      dispatch(setSubmittingBundleUnits(false));
    }
  };
};

export const putBundleUnitAction = ({
  storeId,
  packageId,
  bundleId,
  unitId,
  payload,
}: {
  storeId: number;
  packageId: number;
  bundleId: number;
  unitId: number;
  payload: BundleUnitPayload;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingBundleUnits(true));
      const { data } = await putBundleUnitApi({
        storeId,
        packageId,
        bundleId,
        unitId,
        payload,
      });
      dispatch(setSubmittingBundleUnits(false));
      if (data?.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
      dispatch(setSubmittingBundleUnits(false));
    }
  };
};
type GetCreditPackagePayload = {
  packageId: number;
  storeId: number;
  continutationToken: string;
};
export const getCreditPackagesListAction = ({
  packageId,
  storeId,
  continutationToken,
}: GetCreditPackagePayload) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { creditPackageListRowsPerPage } = getState().packages;
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            limit: creditPackageListRowsPerPage,
            continutationToken,
          },
        });
      dispatch(setCreditPackageListLoading(true));
      const { data } = await getCreditPackagesApi({
        storeId,
        packageId,
        query,
      });
      dispatch(setCreditPackageListLoading(false));
      const creditPackages = data.data;
      const pagination = data.pagination;
      const currencies = data.included.currencies;

      // Update credit package list
      const updatedList = creditPackages.map((creditPackage) => {
        const currency = currencies.find(
          (item) => item.currencyId === creditPackage.currencyId,
        );

        const currencyCode = currency?.localizedCode || currency?.code;

        return {
          ...creditPackage,
          currencyCode,
        };
      });

      dispatch(
        setCreditPackageList({
          list: updatedList,
          pagination,
        }),
      );
      return data.data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setCreditPackageListLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };
};

type PostCreditPackageParams = {
  packageId: number;
  storeId: number;
  payload: CreditPackagePayload;
};
export const postCreditPackageAction =
  ({ packageId, storeId, payload }: PostCreditPackageParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingCreditPackage(true));
      const { data } = await postCreditPackagesApi({
        packageId,
        storeId,
        payload,
      });
      dispatch(setSubmittingCreditPackage(false));
      if (data?.data) {
        dispatch(alertMessage("Created successfully", "success"));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
      dispatch(setSubmittingCreditPackage(false));
    }
  };

export const putCreditPackageAction =
  ({
    packageId,
    creditPackageId,
    storeId,
    payload,
  }: PostCreditPackageParams & { creditPackageId: number }) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingCreditPackage(true));
      const { data } = await putCreditPackagesApi({
        packageId,
        creditPackageId,
        storeId,
        payload,
      });
      dispatch(setSubmittingCreditPackage(false));
      if (data?.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
      dispatch(setSubmittingCreditPackage(false));
    }
  };

export const getCoreMacrosOptionsApiAction = (storeId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await getCoreMacrosOptionsAPI(storeId);
      const data = response.data.data;
      dispatch(setCoreMacrosOptionsData(data));
      return data;
    } catch (error) {
      dispatch(alertMessage("Error fetching core macros options", "error"));
    }
  };
};

export const getCoreMacrosPricingApiAction = (
  storeId: number,
  payload: PostCorePricingApiResponse,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await postCoreMacrosPricingAPI(storeId, payload);
      const data = response.data.data;
      dispatch(setCorePackagePriceData(data));
      return data;
    } catch (error) {
      dispatch(alertMessage("Package Not available", "error"));
    }
  };
};

export const updateSubscriptionMacroMealAction =
  (subscriptionId: number, meals: UpdateMacroMealsPayload) =>
  async (dispatch: any) => {
    try {
      await updateMacroMealsApi(subscriptionId, meals);
      dispatch({
        type: UPDATE_VIEW_SUBSCRIPTION_MEAL,
        payload: meals.meals,
        meta: { viewSubscriptionMealTarget: "macroMeals" },
      });
      dispatch(alertMessage("Updated successfully", "success"));
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
export const getPortionsAction = (brandId: number, type: string) => {
  return async (dispatch: AppDispatch): Promise<PortionData[]> => {
    const filterValue =
      type === PackageType.Meal
        ? PortionFilter.Servings
        : PortionFilter.Macronutrients;

    const query =
      "?" +
      createQuery({
        filter: {
          style: filterValue,
        },
      });

    try {
      const { data } = await getPortionsApi(brandId, query);

      dispatch(
        setPortionList({
          brandId,
          list: data.data,
        }),
      );
      return data.data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const packageGetByIdAction = (
  storeId: number,
  id: number,
  callback?: (data: PackagesInterface | null) => void,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await getPackagesByIdApi(storeId, id);
      if (data.data) {
        if (callback) callback(data.data); // call callback on success
      } else {
        if (callback) callback(null); // call callback with null if no data
      }
      return data.data;
    } catch (error) {
      dispatch(alertMessage("Something went wrong", "error"));
      if (callback) callback(null); // call callback with null on error
    }
  };
};
